import React from 'react';
import type { DebitNoteHistoryEntry } from '../../lib/debitNoteApi';
import { formatDateTime } from '../../utils/dateFormat';

/**
 * What happened to a vendor return, oldest first, in words. The server writes
 * the entries (debitNote.service `historyEntry`); `action` is its stored token.
 */
const ACTION_LABELS: Record<string, string> = {
  created: 'Created',
  edited: 'Edited',
  marked_sent: 'Marked as sent',
  emailed: 'Emailed to vendor',
  vendor_accepted: 'Vendor accepted',
  vendor_refused: 'Vendor refused',
  sent_back: 'Sent back by an Admin',
  cancelled: 'Cancelled',
  approved: 'Approved',
  reversed: 'Reversed',
  vendor_credit_note_attached: "Vendor's credit note attached",
  kept_our_amount: 'Kept our amount',
  use_vendor_amount_requested: "Asked to use the vendor's amount",
  use_vendor_amount_declined: "Vendor's amount declined",
  use_vendor_amount_approved: "Vendor's amount approved",
  goods_released: 'Goods released',
  write_off_requested: 'Write-off requested',
  write_off_approved: 'Write-off approved',
  write_off_declined: 'Write-off declined',
};

/** A token this list does not know yet still reads as words, never as snake_case. */
function actionLabel(action: string): string {
  if (ACTION_LABELS[action]) return ACTION_LABELS[action];
  const words = String(action || '').replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '--';
}

export const DebitNoteHistory: React.FC<{ history: DebitNoteHistoryEntry[] }> = ({ history }) => {
  if (!history.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 text-xs font-semibold text-slate-700">History</div>
      <ol className="space-y-2">
        {history.map((entry, i) => (
          <li key={`${entry.at}-${i}`} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-xs">
            <span className="tabular-nums text-slate-500">{formatDateTime(entry.at, '--')}</span>
            <span className="font-medium text-slate-800">{actionLabel(entry.action)}</span>
            {entry.byName && <span className="text-slate-500">by {entry.byName}</span>}
            {entry.note && <span className="basis-full text-slate-600 sm:basis-auto">{entry.note}</span>}
          </li>
        ))}
      </ol>
    </div>
  );
};

export default DebitNoteHistory;
